/**
 * Lesson Recap tiers — the ONE place the stored↔displayed mapping lives.
 *
 * Stored `documents.visibility` values are FROZEN ('student'/'parent'/'teacher'
 * — every existing recap doc uses them, and the customer-facing documents rules
 * key on them). The redesign renames tiers BY AUDIENCE in the UI only:
 *
 *   student → "Everyone"        student + parents + all three teachers
 *   parent  → "Parents"         parents + teachers; the student doesn't see it
 *   teacher → "Teachers only"   never shown to the family — same lock as a
 *                               captured Song Note
 *
 * SINCE 2026-10-05 (design pass 4 P5.1) NO EDITOR OR HISTORY RENDERS THE
 * `teacher` TIER: that writing lives in the Private file (private/). The entry
 * stays here because the stored value is frozen, the draft hook still iterates
 * all three, and tierByVisibility must keep falling back to the most
 * restrictive tier. RecapTierGroups and previousRecapsModel are where it is
 * left out.
 *
 * Mislabeling here is the exact failure class the redesign exists to kill
 * (confusing the editors sends a parent something written for a teacher), so:
 * never say "notes" unqualified, and never re-derive these labels elsewhere.
 */

export const RECAP_TIERS = [
	{
		visibility: 'student', // stored value — frozen
		key: 'everyone',
		title: 'Recap · Everyone',
		icon: 'fa-users',
		hue: 'info', // --cyv-info banner accent
		privateTier: false,
		whoLine: (studentName, parentName) =>
			`${studentName || 'The student'} + ${parentName ? `${parentName} +` : 'parents +'} all three teachers read this.`,
		placeholder: (studentName) => `${studentName || 'Student'} — how did today go?`,
	},
	{
		visibility: 'parent', // stored value — frozen
		key: 'parents',
		title: 'Recap · Parents',
		icon: 'fa-user-group',
		hue: 'primary', // --cyv-teal-800 banner accent
		privateTier: false,
		whoLine: (studentName) =>
			`Parents + teachers. ${studentName || 'The student'} doesn't see this.`,
		placeholder: (studentName, parentName) => `Anything for ${parentName ? `${parentName}'s` : "the parents'"} eyes…`,
	},
	{
		visibility: 'teacher', // stored value — frozen
		key: 'teachers',
		title: 'Recap · Teachers only',
		icon: 'fa-eye-slash',
		hue: 'muted', // --text-muted, dashed border — the shared privacy grammar
		privateTier: true,
		whoLine: () => 'Never shown to the family — same lock as a captured Song Note.',
		placeholder: () => 'Working notes for the teaching team…',
	},
];

export const tierByVisibility = (visibility) =>
	RECAP_TIERS.find((t) => t.visibility === visibility) || RECAP_TIERS[2]; // default most restrictive

/**
 * STAFF display order (Anthony, 2026-09-25): the teachers' own tier on top, then
 * what goes home, grouped. RECAP_TIERS keeps its original order because it is
 * also the draft/save iteration order and `tierByVisibility`'s fallback index;
 * every staff surface that RENDERS tiers reads these instead.
 */
export const TEACHER_TIER = RECAP_TIERS.find((t) => t.privateTier);
export const FAMILY_TIERS = RECAP_TIERS.filter((t) => !t.privateTier);
export const STAFF_TIER_ORDER = [TEACHER_TIER, ...FAMILY_TIERS].map((t) => t.visibility);
